import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import { WorkflowAction } from 'models/Workflow';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

// The facility-mapping review body: the mapping step halted because a facility string
// the portal named has no saved mapping (or the run collected none at all). One row per
// unmapped string, each picking the Olio facility it ought to map to; saving persists
// the mappings client-wide, so the next run with the same strings passes untouched.

// Value content is camelCased by the API middleware (see eligibilityData.ts).
interface MappedEntry {
  externalValue: string;
  groupId: string;
}

export interface FacilityOption {
  id: string;
  name: string;
  groupType?: string;
}

interface MappingResult {
  unmapped?: string[];
  mapped?: MappedEntry[];
  options?: FacilityOption[];
}

interface Props {
  action: WorkflowAction;
  onComplete: (actionId: string, outputs: Record<string, unknown>) => unknown;
  isCompleting: boolean;
}

const optionLabel = (option: FacilityOption) => option.name;

export default function FacilityMappingReview({ action, onComplete, isCompleting }: Props) {
  const raw = action.outputFor('facility_mapping_result');
  const result: MappingResult = raw && typeof raw === 'object' ? (raw as MappingResult) : {};
  const unmapped = Array.isArray(result.unmapped) ? result.unmapped : [];
  const mapped = Array.isArray(result.mapped) ? result.mapped : [];
  const options = Array.isArray(result.options) ? result.options : [];

  if (unmapped.length > 0) {
    return <UnmappedRows action={action} {...{ unmapped, mapped, options, onComplete, isCompleting }} />;
  }
  return <DirectPick action={action} {...{ options, onComplete, isCompleting }} />;
}

// The main case: every unmapped string needs a pick before the step may pass. The
// picks post as `facility_mappings` — the api persists them and re-resolves
// server-side, so the committed group ids only ever come from the mapping table.
function UnmappedRows({
  action,
  unmapped,
  mapped,
  options,
  onComplete,
  isCompleting,
}: Props & { unmapped: string[]; mapped: MappedEntry[]; options: FacilityOption[] }) {
  const [picks, setPicks] = useState<Record<string, FacilityOption | null>>({});
  const ready = unmapped.every((name) => picks[name]) && !isCompleting;

  const save = () =>
    onComplete(action.id, {
      facility_mappings: unmapped.map((name) => ({
        external_value: name,
        group_id: picks[name]!.id,
      })),
    });

  const nameFor = (groupId: string) => options.find((option) => option.id === groupId)?.name ?? groupId;

  return (
    <Wrapper data-testid='facility-mapping-review'>
      <div>
        <BodySmallBold>
          {unmapped.length} facilit{unmapped.length === 1 ? 'y' : 'ies'} could not be mapped
        </BodySmallBold>
        <Explanation>
          Pick the Olio facility each portal name refers to. The mapping is saved for this portal, so future referrals
          naming the same facility pass through automatically.
        </Explanation>
      </div>
      <Rows>
        {unmapped.map((name) => (
          <Row key={name} data-testid={`facility-mapping-row-${name}`}>
            <ExternalName>{name}</ExternalName>
            <SelectCell>
              <Select<FacilityOption>
                inputId={`facility-mapping-pick-${name}`}
                placeholder='Select a facility'
                options={options}
                value={picks[name] ?? null}
                onChange={(option) => setPicks((prev) => ({ ...prev, [name]: option }))}
                menuPortalTarget={document.body}
                getOptionValue={(option) => option.id}
                getOptionLabel={optionLabel}
              />
            </SelectCell>
          </Row>
        ))}
        {mapped.map((entry) => (
          <Row key={entry.externalValue} data-testid={`facility-mapped-row-${entry.externalValue}`}>
            <ExternalName>{entry.externalValue}</ExternalName>
            <MappedName>{nameFor(entry.groupId)}</MappedName>
          </Row>
        ))}
      </Rows>
      <div>
        <Button size='small' loading={isCompleting} disabled={!ready} onClick={save} data-testid='facility-mapping-save'>
          Save
        </Button>
      </div>
    </Wrapper>
  );
}

// The run collected no facility strings — there's nothing to map, so the person's picks
// ARE the answer: post the chosen facilities directly as `candidate_group_ids`. Still
// n-ary — a referral can go to several facilities.
function DirectPick({
  action,
  options,
  onComplete,
  isCompleting,
}: Props & { options: FacilityOption[] }) {
  const [picks, setPicks] = useState<(FacilityOption | null)[]>([null]);
  const chosen = picks.filter((pick): pick is FacilityOption => !!pick);
  const ready = chosen.length > 0 && !isCompleting;

  const save = () => onComplete(action.id, { candidate_group_ids: chosen.map((pick) => pick.id) });

  return (
    <Wrapper data-testid='facility-mapping-direct-pick'>
      <div>
        <BodySmallBold>The portal named no facilities</BodySmallBold>
        <Explanation>Pick the facility (or facilities) this referral is for.</Explanation>
      </div>
      <Rows>
        {picks.map((pick, index) => (
          // Index keys: rows are positional (no identity beyond their slot).
          <SelectCell key={index}>
            <Select<FacilityOption>
              inputId={`facility-mapping-direct-pick-${index}`}
              placeholder='Select a facility'
              options={options.filter((option) => option.id === pick?.id || !chosen.some((c) => c.id === option.id))}
              value={pick}
              onChange={(option) => setPicks((prev) => prev.map((p, i) => (i === index ? option : p)))}
              menuPortalTarget={document.body}
              getOptionValue={(option) => option.id}
              getOptionLabel={optionLabel}
            />
          </SelectCell>
        ))}
        <AddLink
          type='button'
          onClick={() => setPicks((prev) => [...prev, null])}
          data-testid='facility-mapping-direct-add'>
          + Add facility
        </AddLink>
      </Rows>
      <div>
        <Button
          size='small'
          loading={isCompleting}
          disabled={!ready}
          onClick={save}
          data-testid='facility-mapping-save'>
          Save
        </Button>
      </div>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Explanation = styled(BodySmall)`
  color: ${colors.black50};
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ExternalName = styled(BodySmallBold)`
  flex: 0 0 220px;
  word-break: break-word;
`;

const MappedName = styled(BodySmall)`
  color: ${colors.black50};
`;

const SelectCell = styled.div`
  width: 320px;
`;

const AddLink = styled.button`
  align-self: flex-start;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${colors.blue100};
  font-size: 12px;
  text-decoration: underline;
`;
